import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RewardForm from './components/RewardForm';
import RewardTable from './components/RewardTable';
import RedemptionPanel from './components/RedemptionPanel';
import RewardsAnalytics from './components/RewardsAnalytics';
import {
  getTeacherRewards,
  createReward,
  updateReward,
  deleteReward,
  getTeacherRedemptions,
  updateRedemptionStatus
} from '../../services/rewardService';
import { useAuth } from '../../contexts/AuthContext';

const TABS = [
  { id: 'catalog', label: 'Rewards Catalog', icon: 'Gift' },
  { id: 'redemptions', label: 'Redemption Requests', icon: 'ClipboardList' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart2' },
];

const TeacherRewardsManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('catalog');
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [rewardsRes, redemptionsRes] = await Promise.all([
        getTeacherRewards(user?.id),
        getTeacherRedemptions(user?.id)
      ]);
      setRewards(rewardsRes?.rewards || []);
      setRedemptions(redemptionsRes?.redemptions || []);
    } catch (err) {
      setError('Failed to load rewards data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleCreateReward = async (formData) => {
    const { reward, error } = await createReward(user?.id, formData);
    if (error) {
      setError(error);
      return;
    }
    setRewards([reward, ...rewards]);
    setShowAddForm(false);
    showSuccess('Reward created successfully!');
  };

  const handleUpdateReward = async (rewardId, formData) => {
    const { reward, error } = await updateReward(rewardId, formData);
    if (error) {
      setError(error);
      return;
    }
    setRewards(rewards?.map(r => r?.id === rewardId ? reward : r));
    setEditingReward(null);
    showSuccess('Reward updated successfully!');
  };

  const handleDeleteReward = async (rewardId) => {
    if (!window.confirm('Delete this reward? Students will no longer be able to redeem it.')) return;
    const { error } = await deleteReward(rewardId);
    if (error) {
      setError(error);
      return;
    }
    setRewards(rewards?.filter(r => r?.id !== rewardId));
    showSuccess('Reward deleted.');
  };

  const handleApproveRedemption = async (redemptionId, status, notes) => {
    const { error } = await updateRedemptionStatus(redemptionId, status, notes);
    if (error) {
      setError(error);
      return;
    }
    setRedemptions(redemptions?.map(r =>
      r?.id === redemptionId ? { ...r, status } : r
    ));
    showSuccess(`Redemption ${status}!`);
  };

  const pendingCount = redemptions?.filter(r => r?.status === 'pending')?.length;

  return (
    <>
      <Helmet>
        <title>Rewards Management - LearningTrails</title>
        <meta name="description" content="Manage student rewards and redemption requests" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                  Rewards Management
                </h1>
                <p className="text-muted-foreground text-sm">
                  Create rewards, manage redemption requests, and track student engagement
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  onClick={() => navigate('/classroom-management')}
                >
                  Classroom
                </Button>
                <Button
                  iconName="Plus"
                  onClick={() => { setShowAddForm(true); setActiveTab('catalog'); }}
                >
                  Add Reward
                </Button>
              </div>
            </div>

            {/* Alerts */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <Icon name="AlertCircle" size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
                <button onClick={() => setError(null)}>
                  <Icon name="X" size={16} className="text-red-400" />
                </button>
              </div>
            )}
            {successMsg && (
              <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                <Icon name="CheckCircle2" size={18} className="text-green-600" />
                <p className="text-sm text-green-700">{successMsg}</p>
              </div>
            )}

            {/* Tabs */}
            <div className="flex space-x-1 bg-muted rounded-xl p-1 mb-6 w-fit">
              {TABS?.map(tab => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab?.id
                      ? 'bg-white text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                  {tab?.id === 'redemptions' && pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {pendingCount}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
              </div>
            ) : (
              <>
                {/* Catalog Tab */}
                {activeTab === 'catalog' && (
                  <div className="space-y-6">
                    {(showAddForm || editingReward) && (
                      <RewardForm
                        reward={editingReward}
                        onSubmit={editingReward
                          ? (data) => handleUpdateReward(editingReward?.id, data)
                          : handleCreateReward
                        }
                        onCancel={() => { setShowAddForm(false); setEditingReward(null); }}
                      />
                    )}
                    <RewardTable
                      rewards={rewards}
                      onEdit={(reward) => { setEditingReward(reward); setShowAddForm(false); }}
                      onDelete={handleDeleteReward}
                      onToggleActive={(id, isActive) => handleUpdateReward(id, { isActive })}
                    />
                  </div>
                )}

                {/* Redemptions Tab */}
                {activeTab === 'redemptions' && (
                  <RedemptionPanel
                    redemptions={redemptions}
                    onApprove={(id) => handleApproveRedemption(id, 'approved', '')}
                    onComplete={(id) => handleApproveRedemption(id, 'completed', '')}
                    onReject={(id) => handleApproveRedemption(id, 'rejected', '')}
                  />
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                  <RewardsAnalytics rewards={rewards} redemptions={redemptions} />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default TeacherRewardsManagement;
